import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import MapPin, { MapDot } from "@/components/MapPin";
import MapBubble from "@/components/MapBubble";
import MapFullscreen from "@/components/MapFullscreen";
import { mapUrl } from "@/lib/api";
import { placeDots, placePins, normSize } from "@/lib/mapframe";
import { useTheme } from "@/lib/theme";

const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const TARGET_END = 2.75;
const REVEAL_TAIL = 0.8;

function useSlotSize(ref) {
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    let t;
    const read = () => {
      const r = el.getBoundingClientRect();
      const [sw, sh] = normSize(r.width, r.height);
      setSize((s) => (s && s.sw === sw && s.sh === sh ? s : { cw: r.width, ch: r.height, sw, sh }));
    };
    read();
    const ro = new ResizeObserver(() => {
      clearTimeout(t);
      t = setTimeout(read, 200);
    });
    ro.observe(el);
    return () => {
      clearTimeout(t);
      ro.disconnect();
    };
  }, [ref]);
  return size;
}

function Sweep({ size, visible }) {
  if (!size) return null;
  const { cw, ch } = size;
  const R = 0.46 * Math.hypot(cw, ch);
  const d = Math.ceil(Math.hypot(cw, ch) * 1.05);
  return (
    <div className="sweep-layer pointer-events-none absolute inset-0" style={{ opacity: visible ? 1 : 0 }} data-testid="radar-sweep" aria-hidden="true">
      <svg className="radar-circles absolute inset-0 h-full w-full" viewBox={`0 0 ${cw} ${ch}`}>
        {[0.25, 0.5, 0.75, 1].map((k) => (
          <circle key={k} cx={cw / 2} cy={ch / 2} r={R * k} />
        ))}
      </svg>
      <div className="radar-sweep" style={{ width: d, height: d }} />
    </div>
  );
}

// Map slot: radar sweep while loading, pings on the real positions, then the map fades in and pins drop.
export default function RadarMap({ radarId = null, markers = null, dots = null, cards = null, unlocked = false, onLocked, version, intro = false, interactive = true, onRevealed }) {
  const { theme } = useTheme();
  const slotRef = useRef(null);
  const size = useSlotSize(slotRef);
  const t0 = useRef(0);
  if (!t0.current) {
    t0.current = performance.now();
    if (intro) performance.mark?.("radar-sweep-start");
  }
  const timers = useRef([]);
  const onRevealedRef = useRef(onRevealed);
  onRevealedRef.current = onRevealed;
  const firstVersion = useRef(version);

  const [plan, setPlan] = useState(null);
  const [pingsDone, setPingsDone] = useState(!intro);
  const [shown, setShown] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [sweepGone, setSweepGone] = useState(!intro || reducedMotion());
  const [full, setFull] = useState(false);
  const [tip, setTip] = useState(null);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const target = useMemo(() => {
    if (!size || !radarId || !markers?.length) return null;
    return {
      src: mapUrl(radarId, theme, version, size.sw, size.sh),
      version,
      pins: placePins(markers, size.sw, size.sh, dots || []),
      dots: placeDots(markers, dots || [], size.sw, size.sh),
    };
  }, [size, radarId, markers, dots, theme, version]);
  const cardOf = (rank) => (cards || []).find((c) => String(c.rank) === String(rank));
  const tapPin = (p) => setTip(p.is_user ? null : { kind: "pin", x: p.x, y: p.y, small: !!p.small, card: cardOf(p.label) });
  const tapDot = (d) => setTip({ kind: "dot", x: d.x, y: d.y });
  useEffect(() => {
    if (!tip) return undefined;
    const close = (e) => {
      if (!e.target.closest?.('[data-testid="map-bubble"], .map-pin, .map-dot')) setTip(null);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [tip]);

  // Pings: start once the API answered (>= 0.97 s), faster when the answer came late.
  useEffect(() => {
    if (!intro || plan || !target) return;
    if (reducedMotion()) {
      setPingsDone(true);
      return;
    }
    const tA = (performance.now() - t0.current) / 1000;
    const ps = Math.max(tA, 0.97);
    const n = target.pins.length;
    const iv = Math.min(0.37, Math.max(0.12, (TARGET_END - ps - REVEAL_TAIL) / 3));
    const delay = (ps - tA) * 1000;
    setPlan({ delay, iv: iv * 1000, pins: target.pins });
    later(() => setPingsDone(true), delay + n * iv * 1000);
  }, [intro, plan, target]);

  // Preload then swap (theme, size or V change): no flash, pins move with the image.
  useEffect(() => {
    if (!target) return undefined;
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setShown(target);
    img.src = target.src;
    return () => {
      alive = false;
    };
  }, [target]);

  useEffect(() => {
    if (revealed || !shown || !pingsDone) return;
    setRevealed(true);
    later(() => setSweepGone(true), 450);
    later(() => {
      if (intro) performance.mark?.("radar-landed");
      onRevealedRef.current?.();
    }, reducedMotion() ? 50 : 800);
  }, [revealed, shown, pingsDone]);

  const closeFull = useCallback(() => window.history.back(), []);
  useEffect(() => {
    if (!full) return undefined;
    const onPop = () => setFull(false);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [full]);
  const openFull = () => {
    if (tip) {
      setTip(null);
      return;
    }
    if (!revealed || !interactive || full) return;
    window.history.pushState({ archijoMap: true }, "");
    setFull(true);
  };

  const userChanged = shown && shown.version !== firstVersion.current;

  const legend = revealed && (dots?.length || 0) > 0;
  return (
    <>
    <div
      ref={slotRef}
      data-testid="radar-map"
      data-revealed={revealed ? "true" : "false"}
      onClick={openFull}
      className={`map-slot relative w-full overflow-hidden bg-[color:var(--dot-bg)] md:mx-auto md:rounded-card md:border md:border-line ${
        revealed && interactive ? "cursor-zoom-in" : ""
      }`}
    >
      <div className="dot-plain" aria-hidden="true" />
      {shown && (
        <img
          src={shown.src}
          alt="Carte des architectes les mieux placés sur Google"
          data-testid="radar-map-image"
          data-theme={theme}
          className="map-img absolute inset-0 block h-full w-full select-none object-cover"
          style={{ opacity: revealed ? 1 : 0 }}
          draggable={false}
        />
      )}
      {!sweepGone && <Sweep size={size} visible={!revealed} />}
      {plan &&
        !revealed &&
        plan.pins.map((p, i) => (
          <span key={p.label} data-testid={`radar-ping-${i + 1}`} className="pointer-events-none absolute" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
            <span className="radar-ping-dot" style={{ animationDelay: `${plan.delay + i * plan.iv}ms` }} />
            <span className="radar-ping-ring" style={{ animationDelay: `${plan.delay + i * plan.iv}ms` }} />
          </span>
        ))}
      {revealed &&
        (shown.dots || []).map((d) =>
          unlocked ? (
            <MapPin key={`d${d.rank}`} pin={{ ...d, label: String(d.rank), small: true }} small onTap={interactive ? tapPin : undefined} />
          ) : (
            <MapDot key={`d${d.rank}`} dot={d} onTap={interactive ? tapDot : undefined} />
          ),
        )}
      {revealed &&
        shown.pins.map((p, i) =>
          p.is_user ? (
            <MapPin key={`V-${shown.version}`} pin={p} drop delay={userChanged ? 60 : 100 + i * 90} />
          ) : (
            <MapPin key={p.label} pin={p} drop delay={100 + i * 90} onTap={interactive ? tapPin : undefined} />
          ),
        )}
      {tip && <MapBubble tip={tip} onLocked={() => { setTip(null); onLocked?.(); }} />}
      {revealed && interactive && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openFull();
          }}
          data-testid="map-expand-btn"
          aria-label="Agrandir la carte"
          className="absolute right-2.5 top-2.5 z-[3] grid h-9 w-9 place-items-center rounded-full border border-line bg-surface/95 text-ink transition-transform duration-150 hover:scale-105"
        >
          <Maximize2 className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}
      {full && <MapFullscreen radarId={radarId} markers={markers} dots={dots || []} cards={cards || []} unlocked={unlocked} version={version} theme={theme} onClose={closeFull} />}
    </div>
    {legend && (
      <p className="mt-2 px-5 text-[12px] text-muted md:px-0" data-testid="map-legend">
        {unlocked ? "1 à 10 : visibles" : "1 à 3 : visibles · 4 à 10 : verrouillés"}
      </p>
    )}
    </>
  );
}
